import { formatProjectLabel, formatDatasourceLabel } from '../label-format';

// Link to the CSQ XP app for a given datasource/project id, so users can jump straight to it
// instead of copying the id elsewhere. Hidden until a datasource/project is actually selected.
function csqDatasourceUrl(id) {
  return Number.isFinite(id) && id > 0 ? `https://app.contentsquare.com/#/home?project=${id}` : null;
}

function ScopeSection({
  accounts,
  selectedAccountId,
  setSelectedAccountId,
  allDatasources,
  sourceDatasources,
  targetDatasources,
  sourceDatasourceId,
  targetDatasourceId,
  setTargetDatasourceId,
  resolvedSourceDatasourceId,
  resolvedTargetDatasourceId,
  selectedProject,
  selectedSourceProject,
  resetRun,
}) {
  // Resolved against the FULL datasource list, not the account-filtered dropdown options — the
  // Source can be pre-linked to a datasource outside the currently selected account (e.g. a
  // cross-account link set up before Accounts existed), and it still needs to display formatted
  // correctly even though it wouldn't appear as a selectable option.
  const selectedTargetDatasource = allDatasources.find((d) => String(d.datasourceId) === targetDatasourceId);
  const selectedSourceDatasource = allDatasources.find((d) => String(d.datasourceId) === sourceDatasourceId);

  return (
    <div className="demo-env-mgmt-section">
      {/* Account and Target are always the interactive selects, always in the same place —
          they never move into a column, so reselecting either one doesn't mean hunting for it
          in a different spot once the Source/Target columns below appear. */}
      <div className="demo-env-mgmt-title-with-label">
        <h2 className="demo-env-mgmt-section-title">Scope</h2>
        <label className="demo-env-mgmt-label" htmlFor="scope-account">Account</label>
      </div>
      <div className="demo-env-mgmt-field">
        <select
          id="scope-account"
          className="demo-env-mgmt-select"
          value={selectedAccountId}
          onChange={(e) => { setSelectedAccountId(e.target.value); resetRun(); }}
        >
          <option value="">
            {accounts.length === 0 ? 'No accounts configured yet' : 'Select an account…'}
          </option>
          {accounts.map((account) => (
            <option key={account.id} value={String(account.id)}>{account.label}</option>
          ))}
        </select>
      </div>

      {selectedAccountId && (
        <div className="demo-env-mgmt-field">
          <label className="demo-env-mgmt-label" htmlFor="target-datasource">Target demo environment</label>
          <select
            id="target-datasource"
            className="demo-env-mgmt-select"
            value={targetDatasourceId}
            onChange={(e) => { setTargetDatasourceId(e.target.value); resetRun(); }}
          >
            <option value="">
              {targetDatasources.length === 0 ? 'No target datasources configured yet' : 'Select a datasource…'}
            </option>
            {targetDatasources.map((datasource) => (
              <option key={datasource.id} value={String(datasource.datasourceId)}>{formatDatasourceLabel(datasource)} ({datasource.datasourceId})</option>
            ))}
          </select>
        </div>
      )}

      {selectedAccountId && targetDatasourceId && (
        <div className="demo-env-mgmt-columns">
          {/* Source datasource — read-only, derived from the Target selected above */}
          <div className="demo-env-mgmt-column">
            <h3 className="demo-env-mgmt-column-title">Source</h3>
            <div className="demo-env-mgmt-field">
              <label className="demo-env-mgmt-label">Parent datasource (the source of truth), where entities are created manually in advance.</label>
              <div className="demo-env-mgmt-input" style={{ color: '#555', background: '#f9fafb', cursor: 'default' }}>
                {selectedSourceDatasource
                  ? `${formatDatasourceLabel(selectedSourceDatasource)} (${sourceDatasourceId})`
                  : sourceDatasourceId || '—'}
              </div>
              {selectedSourceProject && (
                <span className="demo-env-mgmt-label">
                  Source Project: {formatProjectLabel(selectedSourceProject)} ({selectedSourceProject.projectId}).
                </span>
              )}
              {csqDatasourceUrl(resolvedSourceDatasourceId) && (
                <a
                  className="demo-env-mgmt-link"
                  href={csqDatasourceUrl(resolvedSourceDatasourceId)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open datasource {resolvedSourceDatasourceId} in CSQ ↗
                </a>
              )}
            </div>
          </div>

          {/* Target datasource — read-only here too, same as Source; the actual select lives above. */}
          <div className="demo-env-mgmt-column">
            <h3 className="demo-env-mgmt-column-title">Target</h3>
            <div className="demo-env-mgmt-field">
              <label className="demo-env-mgmt-label">Target demo environment — the one actually cleared/re-created.</label>
              <div className="demo-env-mgmt-input" style={{ color: '#555', background: '#f9fafb', cursor: 'default' }}>
                {selectedTargetDatasource
                  ? `${formatDatasourceLabel(selectedTargetDatasource)} (${targetDatasourceId})`
                  : targetDatasourceId || '—'}
              </div>
              {selectedProject && (
                <span className="demo-env-mgmt-label">
                  Target Project: {formatProjectLabel(selectedProject)} ({selectedProject.projectId}).
                </span>
              )}
              {csqDatasourceUrl(resolvedTargetDatasourceId) && (
                <a
                  className="demo-env-mgmt-link"
                  href={csqDatasourceUrl(resolvedTargetDatasourceId)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open datasource {resolvedTargetDatasourceId} in CSQ ↗
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ScopeSection;
